import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { objectiveService } from '../../services/objectiveService';
import type { Objective } from '../../models/objective.types';

/**
 * Singleton manager for real-time snapshot subscription
 */
const snapshotManager: {
  unsub: (() => void) | null;
  listeners: number;
} = { unsub: null, listeners: 0 };

/**
 * Hook for real-time objectives query with singleton pattern
 * Subscribes to objectives changes for a specific cycle with optional filters
 *
 * @param cycleId - ID of the cycle to fetch objectives from
 * @param filters - Optional filters for userId, level, and status
 * @param options - Optional configuration (enabled)
 * @returns Objectives data, loading state, and error
 */
export function useObjectivesRealtimeQuery(
  cycleId: string,
  filters?: { userId?: string; level?: number; status?: string },
  options?: { enabled?: boolean }
) {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  const queryResult = useQuery({
    queryKey: ['objectives', cycleId],
    queryFn: async () => {
      // If not enabled or no cycleId, return empty array
      if (!enabled || !cycleId) return [];

      // Try to get cached data first
      const cached = queryClient.getQueryData<Objective[]>(['objectives', cycleId]);
      if (cached && cached.length > 0) {
        return cached;
      }

      // Fetch initial data (no filters - real-time subscription provides all data)
      try {
        const data = await objectiveService.getByCycle(cycleId);
        return data;
      } catch (error) {
        console.error('[ObjectivesQuery] Error fetching objectives:', error);
        throw error;
      }
    },
    staleTime: Infinity, // Always fresh due to real-time updates
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: enabled && !!cycleId,
    retry: 3, // Retry 3 times on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  useEffect(() => {
    // Don't setup subscription if disabled or no cycleId
    if (!enabled || !cycleId) return;

    // Increment listener count
    snapshotManager.listeners += 1;

    // Setup subscription if not already active
    if (!snapshotManager.unsub) {
      snapshotManager.unsub = objectiveService.subscribeToObjectives(cycleId, (objectives) => {
        // Update query cache with real-time data
        // Note: Using cycleId only in queryKey to avoid filter-specific cache issues
        queryClient.setQueryData(['objectives', cycleId], objectives);
      });
    }

    // Cleanup on unmount
    return () => {
      snapshotManager.listeners -= 1;

      // Unsubscribe when no more listeners
      if (snapshotManager.listeners === 0 && snapshotManager.unsub) {
        snapshotManager.unsub();
        snapshotManager.unsub = null;
      }
    };
  }, [cycleId, queryClient, enabled]);

  // Apply client-side filters if provided
  const filteredObjectives = queryResult.data?.filter((obj) => {
    if (filters?.userId && obj.ownerId !== filters.userId) return false;
    if (filters?.level && obj.level !== filters.level) return false;
    if (filters?.status && obj.status !== filters.status) return false;
    return true;
  }) || [];

  return {
    objectives: filteredObjectives,
    isLoading: queryResult.isLoading,
    error: queryResult.error,
  };
}
